import { ThinProfile } from '@/resources/ThinProfile';
import { IThinProfile } from '@/resources/upgrade/ThinProfile';

/**
 * Union type for profiles that can come from either datx (camelCase) or new API (snake_case)
 */
export type AnyProfile = ThinProfile | IThinProfile;

/**
 * Type guard to check if profile is the new IThinProfile format (snake_case)
 */
export function isNewProfileFormat(profile: AnyProfile): profile is IThinProfile {
  return 'first_name' in profile;
}

/**
 * Helper functions to get profile properties regardless of format
 */
export function getProfileFullName(profile: AnyProfile): string {
  if (isNewProfileFormat(profile)) {
    return profile.full_name || '';
  }

  // Old format computes fullName from firstName and lastName
  return [profile.firstName, profile.lastName].filter(Boolean).join(' ');
}

export function getProfileFirstName(profile: AnyProfile): string {
  if (isNewProfileFormat(profile)) {
    return profile.first_name || '';
  }

  return profile.firstName || '';
}

export function getProfileLastName(profile: AnyProfile): string {
  if (isNewProfileFormat(profile)) {
    return profile.last_name || '';
  }

  return profile.lastName || '';
}

export function getProfileAvatar(profile: AnyProfile): string {
  if (isNewProfileFormat(profile)) {
    return profile.avatar || profile.medium_square_avatar_url || '';
  }

  return profile.avatar;
}

export function getProfileSquareAvatar(profile: AnyProfile): string {
  if (isNewProfileFormat(profile)) {
    return profile.square_avatar || profile.medium_square_avatar_url || '';
  }

  return profile.mediumSquareAvatarUrl || '';
}

export function getProfileTitle(profile: AnyProfile): string {
  if (isNewProfileFormat(profile)) {
    return profile.title || '';
  }

  // Old format computes title from organizationalRole and institution
  const parts = [profile.organizationalRole, profile.institution].filter(Boolean);

  return parts.join(' @ ');
}

export function getProfileShortBio(profile: AnyProfile): string {
  if (isNewProfileFormat(profile)) {
    return profile.short_bio || '';
  }

  return profile.shortBio || '';
}

export function getProfileLinkSlug(profile: AnyProfile): string {
  if (isNewProfileFormat(profile)) {
    return profile.link_slug || `${profile.id}-${profile.slug}`;
  }

  return `${profile.id}-${profile.slug}`;
}

export function getProfileEmail(profile: AnyProfile): string {
  return profile.email || '';
}

export function isProfileEmailPublic(profile: AnyProfile): boolean {
  if (isNewProfileFormat(profile)) {
    return profile.email_public || false;
  }

  return profile.emailPublic || false;
}

export function getProfileOrganizationalRole(profile: AnyProfile): string {
  if (isNewProfileFormat(profile)) {
    return profile.organizational_role || '';
  }

  return profile.organizationalRole || '';
}

export function getProfileWebsite(profile: AnyProfile): string {
  return profile.website || '';
}

export function getProfileLinkedin(profile: AnyProfile): string {
  return profile.linkedin || '';
}

export function getProfileOrchid(profile: AnyProfile): string {
  return profile.orchid || '';
}

export function getProfileGoogleScholar(profile: AnyProfile): string {
  if (isNewProfileFormat(profile)) {
    return profile.google_scholar || '';
  }

  return profile.googleScholar || '';
}

export function getProfileTwitter(profile: AnyProfile): string {
  return profile.twitter || '';
}

export function getProfileGithub(profile: AnyProfile): string {
  return profile.github || '';
}
